import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   BusinessDashboard.js
   Tabs: Overview · Loyalty · Membership · Members · Scan · Analytics
   Responsive: sidebar desktop, bottom nav mobile
───────────────────────────────────────────────────────────────────────────── */

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      '#05080f',
  surface: 'rgba(10,16,28,0.9)',
  hard:    '#0a101c',
  border:  'rgba(99,102,241,0.15)',
  borderB: 'rgba(99,102,241,0.4)',
  text:    '#e2e8f0',
  muted:   '#475569',
  dim:     '#1a2540',
  indigo:  '#6366f1',
  violet:  '#8b5cf6',
  green:   '#34d399',
  amber:   '#fbbf24',
  red:     '#f87171',
  cyan:    '#38bdf8',
};

const FONT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fira+Code:wght@400;500&display=swap');`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'}) : '—';
const fmtTime  = d => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '—';
const fmtMoney = n => `$${((n||0)/100).toFixed(2)}`;

function pill(label, color = C.muted) {
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 9px', borderRadius:99,
      background:`${color}22`, color, letterSpacing:0.8, textTransform:'uppercase' }}>
      {label}
    </span>
  );
}

// ── Responsive hook ───────────────────────────────────────────────────────────
function useBreakpoint() {
  const [bp, setBp] = useState(() => window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1100 ? 'tablet' : 'desktop');
  useEffect(() => {
    const fn = () => setBp(window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1100 ? 'tablet' : 'desktop');
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return bp;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function Stat({ icon, label, value, sub, color = C.indigo }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16,
      padding:'18px 20px', position:'relative', overflow:'hidden', backdropFilter:'blur(20px)' }}>
      <div style={{ position:'absolute', inset:0, background:`radial-gradient(ellipse at top right, ${color}0d, transparent 70%)` }} />
      <div style={{ position:'absolute', top:14, right:16, fontSize:22, opacity:0.2 }}>{icon}</div>
      <div style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:'uppercase',
        letterSpacing:1.8, marginBottom:6, fontFamily:'Fira Code, monospace' }}>{label}</div>
      <div style={{ fontSize:28, fontWeight:800, color, fontFamily:'Plus Jakarta Sans, sans-serif',
        letterSpacing:-0.5 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, width=520 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:3000,
      display:'flex', alignItems:'center', justifyContent:'center',
      backdropFilter:'blur(10px)', padding:16 }}>
      <div style={{ background:C.hard, border:`1px solid ${C.borderB}`, borderRadius:20,
        width:`min(${width}px,95vw)`, maxHeight:'90vh', overflowY:'auto',
        boxShadow:`0 0 60px rgba(99,102,241,0.2)` }}>
        <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.border}`,
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{title}</div>
          <button onClick={onClose}
            style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:8,
              color:C.muted, cursor:'pointer', width:30, height:30,
              display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>✕</button>
        </div>
        <div style={{ padding:'20px 22px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────
function Field({ label, value, onChange, type='text', options, rows, placeholder }) {
  const s = { width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${C.border}`,
    borderRadius:10, padding:'10px 14px', color:C.text, fontSize:13, outline:'none',
    boxSizing:'border-box', fontFamily:'inherit', marginTop:4 };
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:10, color:C.muted, textTransform:'uppercase',
        letterSpacing:1.2, display:'block', fontFamily:'Fira Code, monospace' }}>{label}</label>
      {options ? (
        <select value={value} onChange={e=>onChange(e.target.value)} style={s}>
          {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
        </select>
      ) : rows ? (
        <textarea value={value||''} onChange={e=>onChange(e.target.value)} rows={rows}
          placeholder={placeholder} style={{...s, resize:'vertical'}} />
      ) : (
        <input type={type} value={value||''} placeholder={placeholder}
          onChange={e=>onChange(e.target.value)} style={s} />
      )}
    </div>
  );
}

function Btn({ children, onClick, color=C.indigo, outline=false, disabled=false, small=false }) {
  const [h,setH] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ padding: small ? '7px 14px' : '10px 22px',
        borderRadius:10, border: outline ? `1px solid ${color}` : 'none',
        background: outline ? (h?`${color}18`:'transparent') : `linear-gradient(135deg,${color},${color}cc)`,
        color: outline ? color : '#fff', fontWeight:700, cursor:disabled?'not-allowed':'pointer',
        fontSize: small ? 12 : 13, opacity:disabled?0.5:1,
        fontFamily:'Plus Jakarta Sans, sans-serif',
        transform: h&&!disabled ? 'translateY(-1px)' : 'none',
        transition:'all 0.15s', boxShadow: !outline && !disabled && h ? `0 4px 20px ${color}44` : 'none' }}>
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NFC SCAN TERMINAL
// ═══════════════════════════════════════════════════════════════════════════
function ScanTerminal({ businessId, programs, onTap }) {
  const [input, setInput]     = useState('');
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode]       = useState('checkin');  // checkin | redeem
  const [rewardId, setRewardId] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const doTap = useCallback(async (val) => {
    if (!val?.trim()) return;
    setLoading(true); setResult(null);
    try {
      const isUid = val.includes(':');
      const body  = { type: mode, ...(isUid ? { uid: val.trim() } : { flapid: val.trim().toLowerCase() }) };
      if (mode === 'redeem' && rewardId) body.rewardId = rewardId;
      const { data } = await api.post(`/business/${businessId}/tap`, body);
      setResult({ success: true, ...data });
      onTap?.();
      setInput('');
      setTimeout(() => setResult(null), 5000);
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.error || 'Tap failed' });
    }
    setLoading(false);
    inputRef.current?.focus();
  }, [businessId, mode, rewardId, onTap]);

  const handleKey = e => { if (e.key === 'Enter') doTap(input); };

  const rewards = programs?.rewards?.filter(r=>r.isActive) || [];

  return (
    <div style={{ maxWidth:540, margin:'0 auto' }}>
      {/* Mode toggle */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[['checkin','✅ Check-in / Earn'],['redeem','🎁 Redeem Reward']].map(([k,l])=>(
          <button key={k} onClick={()=>setMode(k)}
            style={{ flex:1, padding:'10px', borderRadius:10, border:'none', cursor:'pointer',
              background: mode===k ? `linear-gradient(135deg,${C.indigo},${C.violet})` : 'rgba(255,255,255,0.05)',
              color: mode===k ? '#fff' : C.muted, fontWeight:700, fontSize:13,
              fontFamily:'Plus Jakarta Sans, sans-serif' }}>
            {l}
          </button>
        ))}
      </div>

      {/* Reward selector */}
      {mode === 'redeem' && rewards.length > 0 && (
        <div style={{ marginBottom:16 }}>
          <Field label="Select Reward" value={rewardId} onChange={setRewardId}
            options={[{value:'',label:'— pick reward —'}, ...rewards.map(r=>({value:r._id,label:`${r.name} (${r.pointsCost} pts)` }))]} />
        </div>
      )}

      {/* Scan input */}
      <div style={{ background:C.surface, border:`2px solid ${C.borderB}`, borderRadius:20,
        padding:28, backdropFilter:'blur(20px)',
        boxShadow:`0 0 40px rgba(99,102,241,0.15)` }}>
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontSize:48, marginBottom:8 }}>📲</div>
          <div style={{ fontSize:16, fontWeight:700, color:C.text }}>
            {mode === 'checkin' ? 'Tap NFC Card or Enter Flap ID' : 'Scan Card to Redeem'}
          </div>
          <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
            Enter Flap ID or NFC UID, then press Enter
          </div>
        </div>

        <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="FLP-XXXXXX or 04:A1:B2:C3:D4:E5:F6"
          style={{ width:'100%', background:'rgba(255,255,255,0.06)',
            border:`1px solid ${C.borderB}`, borderRadius:12, padding:'14px 18px',
            color:C.text, fontSize:15, outline:'none', boxSizing:'border-box',
            fontFamily:'Fira Code, monospace', textAlign:'center',
            letterSpacing:1, marginBottom:14 }} />

        <Btn onClick={()=>doTap(input)} disabled={loading||!input.trim()} color={C.indigo}>
          {loading ? 'Processing…' : mode==='checkin' ? '✅ Check In' : '🎁 Redeem'}
        </Btn>
      </div>

      {/* Result */}
      {result && (
        <div style={{ marginTop:20, padding:20, borderRadius:16,
          background: result.success ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)',
          border: `1px solid ${result.success ? C.green : C.red}`,
          animation:'slideUp 0.3s ease' }}>
          {result.success ? (
            <>
              <div style={{ fontSize:16, fontWeight:800, color:C.green, marginBottom:10 }}>
                ✓ {result.action === 'loyalty_redeem' ? 'Reward Redeemed!' : 'Checked In!'}
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:C.text }}>
                {result.member?.name || result.member?.flapid || 'Member'}
              </div>
              <div style={{ display:'flex', gap:16, marginTop:10, flexWrap:'wrap' }}>
                <div style={{ fontSize:12, color:C.muted }}>
                  🏆 Points: <strong style={{color:C.amber}}>{result.member?.loyalty?.points || 0}</strong>
                  {result.pointsDelta !== 0 && (
                    <span style={{ color: result.pointsDelta > 0 ? C.green : C.red, marginLeft:6 }}>
                      {result.pointsDelta > 0 ? '+' : ''}{result.pointsDelta}
                    </span>
                  )}
                </div>
                <div style={{ fontSize:12, color:C.muted }}>
                  📅 Visits: <strong style={{color:C.cyan}}>{result.member?.attendance?.totalVisits || 0}</strong>
                </div>
                <div style={{ fontSize:12, color:C.muted }}>
                  🔥 Streak: <strong style={{color:C.violet}}>{result.member?.attendance?.streak || 0}</strong>
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize:14, color:C.red }}>✗ {result.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MEMBERS LIST
// ═══════════════════════════════════════════════════════════════════════════
function MembersTab({ businessId, plans }) {
  const [members, setMembers]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [q, setQ]               = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm]   = useState({ name:'', email:'', phone:'', flapid:'', planId:'' });
  const [pointsModal, setPointsModal] = useState(null);
  const [ptsDelta, setPtsDelta] = useState(0);
  const [ptsNote, setPtsNote]   = useState('');
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const { data } = await api.get(`/business/${businessId}/members?${params}`);
    setMembers(data.members); setTotal(data.total);
  }, [businessId, q]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (m) => {
    setSelected(m);
    const { data } = await api.get(`/business/${businessId}/members/${m._id}`);
    setDetail(data);
  };

  const addMember = async () => {
    setSaving(true);
    await api.post(`/business/${businessId}/members`, addForm);
    setSaving(false); setAddModal(false); load();
  };

  const adjustPoints = async () => {
    setSaving(true);
    await api.post(`/business/${businessId}/members/${pointsModal._id}/points`, { delta: ptsDelta, note: ptsNote });
    setSaving(false); setPointsModal(null); load();
    if (selected?._id === pointsModal._id) {
      const { data } = await api.get(`/business/${businessId}/members/${pointsModal._id}`);
      setDetail(data);
    }
  };

  const assignPlan = async (memberId, planId) => {
    await api.post(`/business/${businessId}/members/${memberId}/membership`, { planId });
    load();
    if (selected?._id === memberId) {
      const { data } = await api.get(`/business/${businessId}/members/${memberId}`);
      setDetail(data);
    }
  };

  const STATUS_COLORS = { active:C.green, expired:C.red, cancelled:C.muted, pending:C.amber };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input value={q} onChange={e=>setQ(e.target.value)}
          placeholder="Search name / email / flapid…"
          style={{ flex:1, minWidth:200, background:'rgba(255,255,255,0.05)',
            border:`1px solid ${C.border}`, borderRadius:10, padding:'9px 14px',
            color:C.text, fontSize:13, outline:'none' }} />
        <span style={{ fontSize:12, color:C.muted, alignSelf:'center' }}>{total} members</span>
        <Btn onClick={()=>setAddModal(true)} small>+ Add Member</Btn>
      </div>

      {/* Table */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr>{['Member','Flap ID','Points','Visits','Streak','Membership','Last Visit','Actions'].map(h=>(
              <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:10, fontWeight:700,
                textTransform:'uppercase', letterSpacing:1.2, color:C.muted,
                borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap',
                fontFamily:'Fira Code, monospace' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {members.length===0 && <tr><td colSpan={8} style={{textAlign:'center',padding:'40px 0',color:C.dim}}>No members yet</td></tr>}
            {members.map(m=>(
              <tr key={m._id}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                style={{ borderBottom:`1px solid ${C.border}`, cursor:'pointer' }}>
                <td onClick={()=>openDetail(m)} style={{padding:'12px 12px'}}>
                  <div style={{fontWeight:600,color:C.text}}>{m.name||'—'}</div>
                  <div style={{fontSize:11,color:C.muted}}>{m.email||''}</div>
                </td>
                <td style={{padding:'12px 12px',fontFamily:'Fira Code, monospace',color:C.indigo,fontSize:12}}>{m.flapid||'—'}</td>
                <td style={{padding:'12px 12px'}}>
                  <span style={{fontWeight:700,color:C.amber}}>{m.loyalty?.points||0}</span>
                  <span style={{fontSize:10,color:C.muted,marginLeft:4}}>pts</span>
                </td>
                <td style={{padding:'12px 12px',color:C.cyan,fontWeight:600}}>{m.attendance?.totalVisits||0}</td>
                <td style={{padding:'12px 12px',color:C.violet}}>🔥{m.attendance?.streak||0}</td>
                <td style={{padding:'12px 12px'}}>
                  {m.membership?.planName
                    ? <>{pill(m.membership.status, STATUS_COLORS[m.membership.status])} <span style={{fontSize:11,color:C.muted,marginLeft:4}}>{m.membership.planName}</span></>
                    : <span style={{color:C.dim,fontSize:12}}>—</span>}
                </td>
                <td style={{padding:'12px 12px',color:C.muted,fontSize:11}}>{fmtDate(m.attendance?.lastVisit)}</td>
                <td style={{padding:'12px 12px'}}>
                  <button onClick={e=>{e.stopPropagation();setPointsModal(m);setPtsDelta(0);setPtsNote('');}}
                    style={{padding:'4px 10px',borderRadius:6,border:`1px solid ${C.amber}44`,
                      background:'transparent',color:C.amber,cursor:'pointer',fontSize:11,fontWeight:700}}>
                    ± Pts
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Member detail modal */}
      {selected && detail && (
        <Modal title={`${detail.member.name || detail.member.flapid || 'Member'}`}
          onClose={()=>{setSelected(null);setDetail(null);}} width={620}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            {[
              ['Total Points',  detail.member.loyalty?.points||0,          C.amber ],
              ['Lifetime Pts',  detail.member.loyalty?.lifetimePoints||0,  C.amber ],
              ['Total Visits',  detail.member.attendance?.totalVisits||0,  C.cyan  ],
              ['Current Streak',detail.member.attendance?.streak||0,       C.violet],
            ].map(([l,v,col])=>(
              <div key={l} style={{padding:'12px 14px',background:'rgba(255,255,255,0.03)',borderRadius:12,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:1,fontFamily:'Fira Code, monospace'}}>{l}</div>
                <div style={{fontSize:22,fontWeight:800,color:col,marginTop:4}}>{v}</div>
              </div>
            ))}
          </div>

          {/* Assign membership */}
          {plans.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:8,fontFamily:'Fira Code, monospace'}}>
                Assign Membership Plan
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {plans.map(p=>(
                  <button key={p._id} onClick={()=>assignPlan(detail.member._id, p._id)}
                    style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${C.indigo}44`,
                      background: detail.member.membership?.planId===p._id ? `rgba(99,102,241,0.2)` : 'transparent',
                      color: detail.member.membership?.planId===p._id ? C.indigo : C.muted,
                      cursor:'pointer',fontSize:12,fontWeight:600}}>
                    {p.name}
                    {detail.member.membership?.planId===p._id && ' ✓'}
                  </button>
                ))}
              </div>
              {detail.member.membership?.expiryDate && (
                <div style={{fontSize:11,color:C.muted,marginTop:6}}>
                  Expires: {fmtDate(detail.member.membership.expiryDate)}
                </div>
              )}
            </div>
          )}

          {/* Recent taps */}
          <div style={{fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:8,fontFamily:'Fira Code, monospace'}}>
            Recent Activity
          </div>
          <div style={{maxHeight:200,overflowY:'auto'}}>
            {(detail.sessions||[]).map((s,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',
                padding:'8px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
                <div>
                  <span style={{color: s.type==='loyalty_earn'?C.green:s.type==='loyalty_redeem'?C.amber:C.cyan, fontWeight:600}}>
                    {s.type==='checkin'?'Check-in':s.type==='loyalty_earn'?'Earned points':s.type==='loyalty_redeem'?'Redeemed':'Manual'}
                  </span>
                  {s.note && <span style={{color:C.muted,marginLeft:8}}>{s.note}</span>}
                </div>
                <div style={{display:'flex',gap:12,color:C.muted}}>
                  {s.pointsDelta!==0 && <span style={{color:s.pointsDelta>0?C.green:C.red,fontWeight:700}}>
                    {s.pointsDelta>0?'+':''}{s.pointsDelta} pts
                  </span>}
                  <span>{fmtDate(s.timestamp)} {fmtTime(s.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Add member modal */}
      {addModal && (
        <Modal title="Add Member" onClose={()=>setAddModal(false)} width={440}>
          <Field label="Full Name"  value={addForm.name}   onChange={v=>setAddForm(p=>({...p,name:v}))} />
          <Field label="Email"      value={addForm.email}  onChange={v=>setAddForm(p=>({...p,email:v}))} />
          <Field label="Phone"      value={addForm.phone}  onChange={v=>setAddForm(p=>({...p,phone:v}))} />
          <Field label="Flap ID"    value={addForm.flapid} onChange={v=>setAddForm(p=>({...p,flapid:v.toLowerCase()}))} placeholder="Optional — FLP-XXXXXX" />
          {plans.length > 0 && (
            <Field label="Membership Plan" value={addForm.planId} onChange={v=>setAddForm(p=>({...p,planId:v}))}
              options={[{value:'',label:'No plan'}, ...plans.map(p=>({value:p._id,label:p.name}))]} />
          )}
          <Btn onClick={addMember} disabled={saving} color={C.indigo}>
            {saving ? 'Adding…' : 'Add Member'}
          </Btn>
        </Modal>
      )}

      {/* Points modal */}
      {pointsModal && (
        <Modal title={`Adjust Points — ${pointsModal.name||pointsModal.flapid}`} onClose={()=>setPointsModal(null)} width={380}>
          <div style={{fontSize:14,color:C.muted,marginBottom:16}}>
            Current balance: <strong style={{color:C.amber}}>{pointsModal.loyalty?.points||0} pts</strong>
          </div>
          <Field label="Points (+add / -deduct)" value={ptsDelta} onChange={v=>setPtsDelta(Number(v))} type="number" />
          <Field label="Note" value={ptsNote} onChange={setPtsNote} placeholder="Reason for adjustment" />
          <Btn onClick={adjustPoints} disabled={saving||!ptsDelta} color={C.amber}>
            {saving ? 'Saving…' : `Apply ${ptsDelta>0?'+':''}${ptsDelta} Points`}
          </Btn>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOYALTY SETTINGS
// ═══════════════════════════════════════════════════════════════════════════
function LoyaltyTab({ businessId }) {
  const [program, setProgram] = useState(null);
  const [form, setForm]       = useState({});
  const [rewardForm, setRewardForm] = useState({ name:'', description:'', pointsCost:10 });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    const { data } = await api.get(`/business/${businessId}/loyalty`);
    setProgram(data.program);
    if (data.program) setForm({
      name: data.program.name, pointsPerVisit: data.program.pointsPerVisit,
      bonusPoints: data.program.bonusPoints, color: data.program.color,
    });
    else setForm({ name:'Loyalty Program', pointsPerVisit:1, bonusPoints:0, color:'#f59e0b' });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    await api.post(`/business/${businessId}/loyalty`, form);
    setSaving(false); load();
  };

  const addReward = async () => {
    if (!rewardForm.name || !rewardForm.pointsCost) return;
    await api.post(`/business/${businessId}/loyalty/rewards`, rewardForm);
    setRewardForm({ name:'', description:'', pointsCost:10 });
    load();
  };

  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
      {/* Config */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`,
        borderRadius:16, padding:20, backdropFilter:'blur(20px)' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>⚙️ Program Settings</div>
        <Field label="Program Name"       value={form.name}          onChange={v=>f('name',v)} />
        <Field label="Points Per Visit"   value={form.pointsPerVisit} onChange={v=>f('pointsPerVisit',Number(v))} type="number" />
        <Field label="Bonus on First Join" value={form.bonusPoints}  onChange={v=>f('bonusPoints',Number(v))} type="number" />
        <Field label="Brand Color"        value={form.color}         onChange={v=>f('color',v)} type="color" />
        <Btn onClick={save} disabled={saving} color={C.indigo}>
          {saving ? 'Saving…' : program ? 'Update Program' : 'Create Program'}
        </Btn>
      </div>

      {/* Rewards */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`,
        borderRadius:16, padding:20, backdropFilter:'blur(20px)' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>🎁 Reward Tiers</div>
        {(program?.rewards||[]).map((r,i)=>(
          <div key={i} style={{ padding:'10px 12px', background:'rgba(255,255,255,0.03)',
            border:`1px solid ${C.border}`, borderRadius:10, marginBottom:8,
            display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{fontWeight:600,color:C.text,fontSize:13}}>{r.name}</div>
              <div style={{fontSize:11,color:C.muted}}>{r.description}</div>
            </div>
            <span style={{fontWeight:800,color:C.amber,fontFamily:'Fira Code, monospace'}}>{r.pointsCost} pts</span>
          </div>
        ))}
        {(!program?.rewards?.length) && <div style={{color:C.dim,fontSize:12,marginBottom:12}}>No rewards yet</div>}

        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:4}}>
          <div style={{fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:1,marginBottom:10,fontFamily:'Fira Code, monospace'}}>
            Add Reward Tier
          </div>
          <Field label="Reward Name"  value={rewardForm.name}        onChange={v=>setRewardForm(p=>({...p,name:v}))} placeholder="Free Coffee" />
          <Field label="Description"  value={rewardForm.description} onChange={v=>setRewardForm(p=>({...p,description:v}))} placeholder="One free drink" />
          <Field label="Points Cost"  value={rewardForm.pointsCost}  onChange={v=>setRewardForm(p=>({...p,pointsCost:Number(v)}))} type="number" />
          <Btn onClick={addReward} color={C.amber} small disabled={!rewardForm.name}>+ Add Reward</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MEMBERSHIP PLANS
// ═══════════════════════════════════════════════════════════════════════════
function MembershipTab({ businessId, plans, reload }) {
  const [form, setForm] = useState({ name:'', description:'', price:0, duration:30, features:'' });
  const [saving, setSaving] = useState(false);
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    setSaving(true);
    const features = form.features.split('\n').map(s=>s.trim()).filter(Boolean);
    await api.post(`/business/${businessId}/membership-plans`, { ...form, features, price: Number(form.price)*100 });
    setSaving(false); setForm({ name:'', description:'', price:0, duration:30, features:'' }); reload();
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
      {/* Existing plans */}
      <div>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>📋 Active Plans</div>
        {plans.length === 0 && <div style={{color:C.dim,fontSize:12}}>No plans yet</div>}
        {plans.map(p=>(
          <div key={p._id} style={{ padding:'16px', background:C.surface,
            border:`1px solid ${C.border}`, borderRadius:14, marginBottom:12,
            backdropFilter:'blur(20px)' }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <div style={{fontWeight:700,color:C.text,fontSize:14}}>{p.name}</div>
                <div style={{fontSize:12,color:C.muted,marginTop:2}}>{p.description}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:18,fontWeight:800,color:C.indigo}}>{fmtMoney(p.price)}</div>
                <div style={{fontSize:11,color:C.muted}}>{p.duration} days</div>
              </div>
            </div>
            {p.features?.length > 0 && (
              <div style={{marginTop:10}}>
                {p.features.map((ft,i)=>(
                  <div key={i} style={{fontSize:11,color:C.green,marginTop:3}}>✓ {ft}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New plan form */}
      <div style={{ background:C.surface, border:`1px solid ${C.border}`,
        borderRadius:16, padding:20, backdropFilter:'blur(20px)' }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:16 }}>+ New Plan</div>
        <Field label="Plan Name"     value={form.name}        onChange={v=>f('name',v)} placeholder="Gold Member" />
        <Field label="Description"   value={form.description} onChange={v=>f('description',v)} />
        <Field label="Price (USD)"   value={form.price}       onChange={v=>f('price',v)} type="number" placeholder="9.99" />
        <Field label="Duration (days)" value={form.duration}  onChange={v=>f('duration',v)} type="number" />
        <Field label="Features (one per line)" value={form.features} onChange={v=>f('features',v)}
          rows={4} placeholder={"Unlimited access\nFree towel\nGuest pass"} />
        <Btn onClick={save} disabled={saving||!form.name} color={C.indigo}>
          {saving ? 'Creating…' : 'Create Plan'}
        </Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ANALYTICS
// ═══════════════════════════════════════════════════════════════════════════
function AnalyticsTab({ businessId }) {
  const [data, setData]   = useState(null);
  const [days, setDays]   = useState(30);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { res } = await api.get(`/business/${businessId}/analytics?days=${days}`);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [businessId, days]);

  useEffect(() => {
    api.get(`/business/${businessId}/analytics?days=${days}`)
      .then(r => setData(r.data))
      .catch(()=>setData(null))
      .finally(()=>setLoading(false));
  }, [businessId, days]); // eslint-disable-line

  if (loading) return <div style={{color:C.muted,textAlign:'center',paddingTop:60}}>Loading…</div>;
  if (!data)   return <div style={{color:C.dim,textAlign:'center',paddingTop:60}}>No analytics data yet</div>;

  const { summary, recentSessions, topMembers, dailyTrend } = data;
  const maxVisits = Math.max(...(dailyTrend||[]).map(d=>d.visits||0), 1);

  return (
    <div>
      {/* Period */}
      <div style={{display:'flex',gap:8,marginBottom:20}}>
        {[7,30,90].map(d=>(
          <button key={d} onClick={()=>setDays(d)}
            style={{padding:'6px 16px',borderRadius:99,border:'none',cursor:'pointer',
              background: days===d ? C.indigo : 'rgba(255,255,255,0.06)',
              color: days===d ? '#fff' : C.muted,
              fontWeight:700,fontSize:12,fontFamily:'Fira Code, monospace'}}>
            {d}d
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:14,marginBottom:24}}>
        <Stat icon="👥" label="Total Members"   value={summary.totalMembers}   color={C.indigo} />
        <Stat icon="✅" label="Active Members"  value={summary.activeMembers}  color={C.green}  />
        <Stat icon="📲" label="Check-ins"       value={summary.totalSessions}  color={C.cyan}   />
        <Stat icon="🏆" label="Points in Circ." value={summary.totalPoints}    color={C.amber}  />
      </div>

      {/* Daily trend */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,
        padding:20,marginBottom:20,backdropFilter:'blur(20px)'}}>
        <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:16}}>Daily Check-ins</div>
        <div style={{display:'flex',alignItems:'flex-end',gap:3,height:80}}>
          {(dailyTrend||[]).map((d,i)=>(
            <div key={i} title={`${d._id}: ${d.visits} visits`}
              style={{flex:1,borderRadius:'3px 3px 0 0',minWidth:3,
                background:`rgba(99,102,241,${0.2+0.8*(d.visits/maxVisits)})`,
                height:`${Math.max(4,(d.visits/maxVisits)*100)}%`,
                transition:'height 0.5s ease', transitionDelay:`${i*10}ms`}} />
          ))}
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
        {/* Top members */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,backdropFilter:'blur(20px)'}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>🏆 Top Members</div>
          {(topMembers||[]).map((m,i)=>(
            <div key={m._id} style={{display:'flex',alignItems:'center',gap:12,
              padding:'8px 0',borderBottom:`1px solid ${C.border}`}}>
              <div style={{width:24,height:24,borderRadius:8,background:`rgba(99,102,241,0.15)`,
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:11,fontWeight:800,color:C.indigo}}>{i+1}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{m.name||m.flapid||'—'}</div>
                <div style={{fontSize:11,color:C.muted}}>{m.attendance?.totalVisits||0} visits</div>
              </div>
              <span style={{fontWeight:800,color:C.amber,fontSize:14}}>{m.loyalty?.points||0} pts</span>
            </div>
          ))}
        </div>

        {/* Recent sessions */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,backdropFilter:'blur(20px)'}}>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>🕐 Recent Activity</div>
          {(recentSessions||[]).slice(0,8).map((s,i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',
              padding:'7px 0',borderBottom:`1px solid ${C.border}`,fontSize:12}}>
              <div>
                <span style={{color:C.text,fontWeight:600}}>{s.memberName||s.flapid||'—'}</span>
                <span style={{color:C.muted,marginLeft:8,fontSize:11}}>
                  {s.type==='checkin'?'checked in':s.type==='loyalty_earn'?'earned points':'redeemed'}
                </span>
              </div>
              <span style={{color:C.dim,fontSize:11}}>{fmtTime(s.timestamp)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN SHELL
// ═══════════════════════════════════════════════════════════════════════════
const TABS = [
  { key:'overview',   label:'📊 Overview'    },
  { key:'products',   label:'🛍️ Products'    },
  { key:'orders',     label:'📦 Orders'      },
  { key:'scan',       label:'📲 Scan'        },
  { key:'members',    label:'👥 Members'     },
  { key:'loyalty',    label:'🏆 Loyalty'     },
  { key:'membership', label:'🎫 Membership'  },
  { key:'devices',    label:'📡 Devices'     },
  { key:'bookings',   label:'📅 Bookings'    },
  { key:'analytics',  label:'📈 Analytics'   },
];

export default function BusinessDashboard() {
  const navigate    = useNavigate();
  const { id }      = useParams();   // /business/:id
  const bp          = useBreakpoint();
  const [tab, setTab]           = useState('overview');
  const [business, setBusiness] = useState(null);
  const [plans, setPlans]       = useState([]);
  const [loyaltyProgram, setLoyaltyProgram] = useState(null);
  const [summary, setSummary]   = useState(null);
  // Products tab
  const [products,   setProducts]   = useState([]);
  const [prodLoading,setProdLoading] = useState(false);
  const [showProdForm, setShowProdForm] = useState(false);
  const [editProd,   setEditProd]   = useState(null);
  const [prodForm,   setProdForm]   = useState({ name:'', description:'', category:'', price:'', stock:'', currency:'NPR', isFeatured:false });
  const [prodSaving, setProdSaving] = useState(false);
  // Orders tab
  const [orders,     setOrders]     = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  // Devices tab
  const [devices,    setDevices]    = useState([]);
  // Bookings tab
  const [bookings,   setBookings]   = useState([]);
  const [bookingDate, setBookingDate] = useState(new Date().toISOString().slice(0,10));
  const [loading, setLoading]   = useState(true);

  const loadBusiness = useCallback(async () => {
    if (!id) return;
    try {
      const [bRes, pRes, lRes] = await Promise.all([
        api.get(`/business/${id}`),
        api.get(`/business/${id}/membership-plans`),
        api.get(`/business/${id}/loyalty`),
      ]);
      setBusiness(bRes.data.business);
      setPlans(pRes.data.plans);
      setLoyaltyProgram(lRes.data.program);
    } catch { navigate('/dashboard'); }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadBusiness(); }, [loadBusiness]);

  // Quick summary for overview
  useEffect(() => {
    if (!id) return;
    api.get(`/business/${id}/analytics?days=30`)
      .then(r => setSummary(r.data.summary))
      .catch(() => {});
  }, [id]);

  if (loading) return (
    <div style={{minHeight:'100vh',background:C.bg,display:'flex',alignItems:'center',
      justifyContent:'center',color:C.muted,fontFamily:'Plus Jakarta Sans, sans-serif'}}>
      Loading…
    </div>
  );

  const isMobile  = bp === 'mobile';
  const isDesktop = bp === 'desktop';

  const tabContent = {
    overview: (
      <div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:14,marginBottom:24}}>
          <Stat icon="👥" label="Total Members" value={summary?.totalMembers||0}  color={C.indigo} />
          <Stat icon="✅" label="Active"        value={summary?.activeMembers||0} color={C.green} />
          <Stat icon="📲" label="Check-ins (30d)" value={summary?.totalSessions||0} color={C.cyan} />
          <Stat icon="🏆" label="Points Given"  value={summary?.totalPoints||0}   color={C.amber} />
        </div>
        <div style={{display:'grid',gridTemplateColumns:isDesktop?'1fr 1fr':'1fr',gap:16}}>
          {/* Modules */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,backdropFilter:'blur(20px)'}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>Active Modules</div>
            {[
              ['🏆 Loyalty Program',   business?.modules?.loyalty,    ()=>setTab('loyalty')   ],
              ['🎫 Membership Plans',  business?.modules?.membership, ()=>setTab('membership')],
              ['📅 Attendance Tracking', true,                        ()=>setTab('members')   ],
            ].map(([label, active, go])=>(
              <div key={label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'10px 0',borderBottom:`1px solid ${C.border}`}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:active?C.green:C.dim}} />
                  <span style={{fontSize:13,color:C.text}}>{label}</span>
                </div>
                <button onClick={go}
                  style={{padding:'4px 12px',borderRadius:7,border:`1px solid ${C.border}`,
                    background:'transparent',color:C.muted,cursor:'pointer',fontSize:11}}>
                  {active ? 'Manage' : 'Set Up'}
                </button>
              </div>
            ))}
          </div>
          {/* Quick actions */}
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:20,backdropFilter:'blur(20px)'}}>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14}}>Quick Actions</div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <Btn onClick={()=>setTab('scan')}    color={C.indigo}>📲 Open Scan Terminal</Btn>
              <Btn onClick={()=>setTab('members')} color={C.violet} outline>👥 View All Members</Btn>
              <Btn onClick={()=>navigate('/dashboard')} color={C.muted} outline>← Back to Dashboard</Btn>
            </div>
          </div>
        </div>
      </div>
    ),
    scan:       <ScanTerminal businessId={id} programs={loyaltyProgram} onTap={loadBusiness} />,
    members:    <MembersTab businessId={id} plans={plans} />,
    loyalty:    <LoyaltyTab businessId={id} />,
    membership: <MembershipTab businessId={id} plans={plans} reload={loadBusiness} />,
    analytics:  <AnalyticsTab businessId={id} />,
  
    products: (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
          <div style={{fontSize:16,fontWeight:700,color:C.text}}>Product Catalog</div>
          <button onClick={()=>{setProdForm({name:'',description:'',category:'',price:'',stock:'',currency:'NPR',isFeatured:false});setEditProd(null);setShowProdForm(true);}}
            style={{padding:'8px 16px',borderRadius:9,border:'none',background:C.indigo,color:'#fff',fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13,fontWeight:700,cursor:'pointer'}}>
            + Add Product
          </button>
        </div>
        {showProdForm && (
          <div style={{background:C.surface,border:`1px solid ${C.borderB}`,borderRadius:14,padding:20,marginBottom:18}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:14}}>{editProd?'Edit Product':'New Product'}</div>
            {[['name','Name *'],['description','Description'],['category','Category (e.g. drinks)'],['price','Price (in paisa, e.g. 35000 = NPR 350)'],['stock','Stock (leave blank = unlimited)']].map(([field,label])=>(
              <div key={field} style={{marginBottom:12}}>
                <div style={{fontSize:10,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:1.5,marginBottom:5}}>{label}</div>
                {field==='description'
                  ? <textarea value={prodForm[field]||''} onChange={e=>setProdForm(p=>({...p,[field]:e.target.value}))} rows={2}
                      style={{width:'100%',padding:'9px 12px',background:'rgba(255,255,255,.05)',border:`1px solid ${C.border}`,borderRadius:9,color:C.text,fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13,outline:'none',resize:'vertical'}}/>
                  : <input value={prodForm[field]||''} onChange={e=>setProdForm(p=>({...p,[field]:e.target.value}))} type={field==='price'||field==='stock'?'number':'text'}
                      style={{width:'100%',padding:'9px 12px',background:'rgba(255,255,255,.05)',border:`1px solid ${C.border}`,borderRadius:9,color:C.text,fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13,outline:'none'}}/>
                }
              </div>
            ))}
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
              <input type="checkbox" checked={!!prodForm.isFeatured} onChange={e=>setProdForm(p=>({...p,isFeatured:e.target.checked}))} id="feat"/>
              <label htmlFor="feat" style={{fontSize:13,color:C.muted,cursor:'pointer'}}>Featured product</label>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>{setShowProdForm(false);setEditProd(null);}} style={{flex:1,padding:9,borderRadius:8,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer',fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13}}>Cancel</button>
              <button disabled={prodSaving||!prodForm.name.trim()||!prodForm.price} onClick={async()=>{
                setProdSaving(true);
                try {
                  const payload = {...prodForm, price: parseInt(prodForm.price)||0, stock: prodForm.stock===''?null:parseInt(prodForm.stock)};
                  if (editProd) await api.put(`/business/${id}/products/${editProd._id}`, payload);
                  else { const r = await api.post(`/business/${id}/products`, payload); setProducts(p=>[r.data,...p]); }
                  setShowProdForm(false); setEditProd(null);
                  if (editProd) setProducts(p=>p.map(x=>x._id===editProd._id?{...x,...payload}:x));
                } catch(e){ alert(e.response?.data?.error||e.message); }
                setProdSaving(false);
              }} style={{flex:2,padding:9,borderRadius:8,border:'none',background:C.indigo,color:'#fff',cursor:'pointer',fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13,fontWeight:700,opacity:prodSaving?.6:1}}>
                {prodSaving?'Saving…':editProd?'Save Changes':'Add Product'}
              </button>
            </div>
          </div>
        )}
        {products.length===0 ? (
          <div style={{textAlign:'center',padding:40,color:C.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>🛍️</div>
            <div>No products yet. Add your first product!</div>
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:12}}>
            {products.map(p=>(
              <div key={p._id} style={{background:C.surface,border:`1px solid ${p.isFeatured?C.amber:C.border}`,borderRadius:14,overflow:'hidden'}}>
                {p.images?.[0] && <img src={p.images[0]} alt={p.name} style={{width:'100%',height:110,objectFit:'cover'}}/>}
                <div style={{padding:12}}>
                  {p.isFeatured && <div style={{fontSize:9,color:C.amber,fontWeight:700,marginBottom:4}}>⭐ FEATURED</div>}
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>{p.name}</div>
                  <div style={{fontSize:12,color:C.indigo,fontWeight:700}}>{p.currency} {((p.price||0)/100).toFixed(2)}</div>
                  {p.category && <div style={{fontSize:10,color:C.muted,marginTop:2}}>{p.category}</div>}
                  {p.stock!==null && <div style={{fontSize:10,color:p.stock>0?C.green:C.red,marginTop:2}}>{p.stock>0?`${p.stock} in stock`:'Out of stock'}</div>}
                  <div style={{display:'flex',gap:6,marginTop:10}}>
                    <button onClick={()=>{setEditProd(p);setProdForm({name:p.name,description:p.description||'',category:p.category||'',price:String(p.price||0),stock:p.stock===null?'':String(p.stock),currency:p.currency||'NPR',isFeatured:!!p.isFeatured});setShowProdForm(true);}}
                      style={{flex:1,padding:'6px',borderRadius:7,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer',fontSize:11,fontFamily:'Plus Jakarta Sans,sans-serif'}}>Edit</button>
                    <button onClick={async()=>{if(!window.confirm('Delete product?'))return;await api.delete(`/business/${id}/products/${p._id}`);setProducts(ps=>ps.filter(x=>x._id!==p._id));}}
                      style={{flex:1,padding:'6px',borderRadius:7,border:`1px solid rgba(248,113,113,.25)`,background:'rgba(248,113,113,.08)',color:C.red,cursor:'pointer',fontSize:11,fontFamily:'Plus Jakarta Sans,sans-serif'}}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    ),

    orders: (
      <div>
        <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:18}}>Orders</div>
        {orders.length===0 ? (
          <div style={{textAlign:'center',padding:40,color:C.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>📦</div>
            <div>No orders yet</div>
            {!orders._loaded && <button onClick={async()=>{setOrderLoading(true);const r=await api.get(`/storefront/orders/business/${id}`);setOrders(r.data||[]);orders._loaded=true;setOrderLoading(false);}} style={{marginTop:12,padding:'8px 18px',borderRadius:9,border:'none',background:C.indigo,color:'#fff',cursor:'pointer',font:'700 13px Plus Jakarta Sans,sans-serif'}}>Load Orders</button>}
          </div>
        ) : orders.map(o=>(
          <div key={o._id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:10}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>{o.orderNumber}</div>
                <div style={{fontSize:11,color:C.muted,marginTop:2}}>{new Date(o.createdAt).toLocaleString()}</div>
              </div>
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <span style={{fontSize:10,padding:'3px 9px',borderRadius:99,fontWeight:700,background:o.status==='delivered'?'rgba(52,211,153,.12)':o.status==='pending'?'rgba(251,191,36,.12)':'rgba(99,102,241,.12)',color:o.status==='delivered'?C.green:o.status==='pending'?C.amber:C.indigo}}>{o.status.toUpperCase()}</span>
                <span style={{fontSize:13,fontWeight:700,color:C.text}}>{o.currency} {((o.total||0)/100).toFixed(2)}</span>
              </div>
            </div>
            <div style={{fontSize:12,color:C.muted,marginBottom:8}}>{(o.items||[]).map(i=>`${i.productName} ×${i.qty}`).join(' · ')}</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {['pending','confirmed','preparing','ready','delivered'].map(s=>(
                <button key={s} onClick={async()=>{await api.put(`/storefront/orders/${o._id}/status`,{status:s});setOrders(os=>os.map(x=>x._id===o._id?{...x,status:s}:x));}}
                  style={{padding:'4px 10px',borderRadius:7,border:`1px solid ${C.border}`,background:o.status===s?C.indigo:'transparent',color:o.status===s?'#fff':C.muted,cursor:'pointer',fontSize:10,fontFamily:'Plus Jakarta Sans,sans-serif',fontWeight:o.status===s?700:400}}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
        {orders.length===0 && <button onClick={async()=>{setOrderLoading(true);try{const r=await api.get(`/storefront/orders/business/${id}`);setOrders(r.data||[]);}catch{}setOrderLoading(false);}} style={{marginTop:12,padding:'8px 18px',borderRadius:9,border:'none',background:C.indigo,color:'#fff',cursor:'pointer',font:'700 13px Plus Jakarta Sans,sans-serif',display:'flex',alignItems:'center',gap:6}}>
          {orderLoading?'⏳ Loading…':'📦 Load Orders'}
        </button>}
      </div>
    ),

    devices: (
      <div>
        <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:6}}>NFC Reader Devices</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:18,lineHeight:1.6}}>
          Pair your ESP8266 NFC reader. Set <code style={{color:C.cyan,background:'rgba(56,189,248,.08)',padding:'1px 6px',borderRadius:4}}>BUSINESS_ID</code> in your firmware to this business's MongoDB ID.
        </div>
        <div style={{background:'rgba(99,102,241,.08)',border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:18,fontFamily:'Fira Code,monospace',fontSize:12,color:C.cyan}}>
          <div style={{color:C.muted,fontSize:10,marginBottom:8}}>// Paste into flap_nfc_reader.ino</div>
          <div>const char* BUSINESS_ID = "{id}";</div>
          <div>const char* API_KEY = "flap-key-001"; // from .env DEVICE_API_KEYS</div>
          <div>const char* API_BASE = "http://savps.xyz:30161";</div>
        </div>
        {devices.length===0 ? (
          <div style={{textAlign:'center',padding:40,color:C.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>📡</div>
            <div>No devices registered yet</div>
            <div style={{fontSize:12,marginTop:8}}>Flash the firmware above and power on your reader</div>
          </div>
        ) : devices.map(d=>(
          <div key={d.deviceId} style={{background:C.surface,border:`1px solid ${d.isActive?C.borderB:C.border}`,borderRadius:12,padding:14,marginBottom:10,display:'flex',gap:12,alignItems:'flex-start'}}>
            <div style={{fontSize:28}}>📡</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{d.name||d.deviceId}</div>
              <div style={{fontSize:11,color:C.muted,marginTop:2}}>ID: {d.deviceId} · {d.type} · FW {d.firmwareVer||'?'}</div>
              {d.location && <div style={{fontSize:11,color:C.muted}}>📍 {d.location}</div>}
              <div style={{fontSize:10,marginTop:4,display:'flex',gap:8}}>
                <span style={{color:d.isActive?C.green:C.red}}>{d.isActive?'● Online':'○ Offline'}</span>
                {d.lastSeen && <span style={{color:C.muted}}>Last seen: {new Date(d.lastSeen).toLocaleString()}</span>}
                {d.lastIp   && <span style={{color:C.muted}}>IP: {d.lastIp}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    ),

    bookings: (
      <div>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
          <div style={{fontSize:16,fontWeight:700,color:C.text}}>Bookings</div>
          <input type="date" value={bookingDate} onChange={async e=>{setBookingDate(e.target.value);try{const r=await api.get(`/bookings/business/${id}?date=${e.target.value}`);setBookings(r.data||[]);}catch{}}}
            style={{padding:'7px 12px',borderRadius:8,border:`1px solid ${C.border}`,background:'rgba(255,255,255,.05)',color:C.text,fontFamily:'Plus Jakarta Sans,sans-serif',fontSize:13,outline:'none',cursor:'pointer'}}/>
        </div>
        {bookings.length===0 ? (
          <div style={{textAlign:'center',padding:40,color:C.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>📅</div>
            <div>No bookings for this date</div>
            <button onClick={async()=>{try{const r=await api.get(`/bookings/business/${id}?date=${bookingDate}`);setBookings(r.data||[]);}catch{}}} style={{marginTop:12,padding:'8px 18px',borderRadius:9,border:'none',background:C.indigo,color:'#fff',cursor:'pointer',font:'700 13px Plus Jakarta Sans,sans-serif'}}>Load Bookings</button>
          </div>
        ) : bookings.map(b=>(
          <div key={b._id} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:10}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{b.userName||'Customer'}</div>
              <span style={{fontSize:10,padding:'3px 9px',borderRadius:99,fontWeight:700,background:b.status==='confirmed'?'rgba(52,211,153,.12)':b.status==='pending'?'rgba(251,191,36,.12)':'rgba(248,113,113,.12)',color:b.status==='confirmed'?C.green:b.status==='pending'?C.amber:C.red}}>{b.status.toUpperCase()}</span>
            </div>
            <div style={{fontSize:12,color:C.muted}}>{new Date(b.date).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})} · {b.durationMin}min · {b.serviceName||'Appointment'}</div>
            {b.notes && <div style={{fontSize:11,color:C.muted,marginTop:4}}>Note: {b.notes}</div>}
            <div style={{display:'flex',gap:6,marginTop:10}}>
              {['confirmed','completed','no_show','cancelled'].map(s=>(
                <button key={s} onClick={async()=>{await api.put(`/bookings/${b._id}/status`,{status:s});setBookings(bs=>bs.map(x=>x._id===b._id?{...x,status:s}:x));}}
                  style={{padding:'4px 8px',borderRadius:7,border:`1px solid ${C.border}`,background:b.status===s?C.indigo:'transparent',color:b.status===s?'#fff':C.muted,cursor:'pointer',fontSize:10,fontFamily:'Plus Jakarta Sans,sans-serif'}}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    ),

};

  return (
    <div style={{minHeight:'100vh',background:C.bg,color:C.text,
      fontFamily:'Plus Jakarta Sans, sans-serif',
      display:'flex',flexDirection:isDesktop?'row':'column'}}>
      <style>{`
        ${FONT}
        * { box-sizing:border-box; }
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-thumb{background:rgba(99,102,241,0.3);border-radius:99px}
        select option { background:#0a101c; }
        @keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
      `}</style>

      {/* Sidebar — desktop */}
      {isDesktop && (
        <div style={{width:220,background:'rgba(5,8,15,0.97)',borderRight:`1px solid ${C.border}`,
          display:'flex',flexDirection:'column',padding:'20px 12px',flexShrink:0}}>
          <div style={{padding:'8px 16px 20px',borderBottom:`1px solid ${C.border}`,marginBottom:12}}>
            <div style={{fontSize:11,color:C.muted,letterSpacing:2,fontFamily:'Fira Code, monospace'}}>BUSINESS</div>
            <div style={{fontSize:15,fontWeight:800,color:C.text,marginTop:2,lineHeight:1.3}}>
              {business?.name || '—'}
            </div>
            {pill(business?.category||'other', C.indigo)}
          </div>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{padding:'10px 16px',borderRadius:10,border:'none',cursor:'pointer',
                background: tab===t.key ? 'rgba(99,102,241,0.15)' : 'transparent',
                color: tab===t.key ? C.indigo : C.muted,
                fontWeight:tab===t.key?700:500, fontSize:13, textAlign:'left', width:'100%',
                outline: tab===t.key ? `1px solid rgba(99,102,241,0.3)` : 'none',
                fontFamily:'Plus Jakarta Sans, sans-serif'}}>
              {t.label}
            </button>
          ))}
          <div style={{marginTop:'auto',paddingTop:16,borderTop:`1px solid ${C.border}`}}>
            <button onClick={()=>navigate('/dashboard')}
              style={{width:'100%',padding:'9px 16px',borderRadius:10,
                border:`1px solid rgba(248,113,113,0.2)`,
                background:'rgba(248,113,113,0.06)',color:C.red,
                cursor:'pointer',fontSize:13,fontWeight:600,
                fontFamily:'Plus Jakarta Sans, sans-serif'}}>
              ← Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0}}>
        {/* Header */}
        <div style={{padding: isMobile?'12px 16px':'16px 28px',
          borderBottom:`1px solid ${C.border}`,
          display:'flex',alignItems:'center',justifyContent:'space-between',
          background:'rgba(5,8,15,0.95)',backdropFilter:'blur(20px)',flexShrink:0}}>
          <div>
            <div style={{fontSize:isMobile?15:18,fontWeight:800,color:C.text}}>
              {TABS.find(t=>t.key===tab)?.label || 'Business'}
            </div>
            <div style={{fontSize:11,color:C.muted,fontFamily:'Fira Code, monospace'}}>
              {business?.name}
            </div>
          </div>
          {isMobile && (
            <button onClick={()=>navigate('/dashboard')}
              style={{padding:'7px 12px',borderRadius:8,border:`1px solid ${C.border}`,
                background:'transparent',color:C.muted,cursor:'pointer',fontSize:12}}>
              ← Back
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{flex:1,overflowY:'auto',padding:isMobile?'16px':'24px 28px'}}>
          {tabContent[tab]}
        </div>
      </div>

      {/* Bottom nav — mobile/tablet */}
      {!isDesktop && (
        <div style={{background:'rgba(5,8,15,0.97)',borderTop:`1px solid ${C.border}`,
          display:'flex',padding:'6px 4px',zIndex:50,flexShrink:0}}>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',
                gap:3,padding:'6px 4px',border:'none',cursor:'pointer',background:'transparent'}}>
              <span style={{fontSize:18,filter:tab===t.key?`drop-shadow(0 0 4px ${C.indigo})`:undefined}}>
                {t.label.split(' ')[0]}
              </span>
              <span style={{fontSize:9,color:tab===t.key?C.indigo:C.muted,
                fontFamily:'Fira Code, monospace',textTransform:'uppercase'}}>
                {t.label.split(' ').slice(1).join(' ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}